import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Database, FileUp, Loader2, Send } from "lucide-react";
import { api } from "../lib/api";
import { DATAFORFRAGAN_TYPER } from "../lib/constants";

export default function Insamling() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading", data: null });
    const [pasteText, setPasteText] = useState("");
    const [filnamn, setFilnamn] = useState("");
    const [consent, setConsent] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        api.get(`/public/insamling/${token}`)
            .then((r) => setState({ status: "ok", data: r.data }))
            .catch(() => setState({ status: "expired", data: null }));
    }, [token]);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setPasteText(String(reader.result));
            setFilnamn(file.name);
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    const submit = async () => {
        setError("");
        if (!pasteText.trim()) {
            setError("Ladda upp en fil eller klistra in data först.");
            return;
        }
        if (!consent) {
            setError("Samtycke krävs – kryssa i rutan innan du skickar.");
            return;
        }
        setSending(true);
        try {
            await api.post(`/public/insamling/${token}/upload`, {
                filnamn: filnamn || "inklistrad-data.txt", text: pasteText, samtycke: consent,
            }, { timeout: 180000 });
            setDone(true);
        } catch (err) {
            setError(err?.response?.data?.detail || "Uppladdningen misslyckades – försök igen.");
        } finally {
            setSending(false);
        }
    };

    const typInfo = DATAFORFRAGAN_TYPER[state.data?.typ];

    return (
        <div className="min-h-screen canvas-bg flex items-center justify-center px-4 py-10" data-testid="insamling-page">
            <div className="w-full max-w-xl">
                <div className="glass-panel rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-center gap-3 mb-5">
                        <img src="/hamn-emblem.png" alt="HAMN" className="w-10 h-10 rounded-full border border-current/40 object-cover" />
                        <div>
                            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Säker dataförfrågan</p>
                            <h1 className="font-display font-extrabold text-xl tracking-tight text-slate-100">
                                {state.data?.title || "Dataförfrågan"}
                            </h1>
                        </div>
                    </div>

                    {state.status === "loading" && (
                        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Laddar …</div>
                    )}

                    {state.status === "expired" && (
                        <p data-testid="insamling-expired" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
                            Den här länken har gått ut eller stängts. Kontakta din konsult för en ny länk.
                        </p>
                    )}

                    {state.status === "ok" && !done && (
                        <div className="space-y-4">
                            {typInfo && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
                                    <p className="text-xs font-semibold text-amber-300 flex items-center gap-1.5">
                                        <Database className="w-3.5 h-3.5" /> {typInfo.label}
                                    </p>
                                    <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{typInfo.hint}</p>
                                </div>
                            )}
                            {state.data.instruktion && (
                                <p className="text-sm text-slate-300 leading-relaxed">{state.data.instruktion}</p>
                            )}
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Ladda upp en export (CSV/TXT) eller klistra in datan. Innan något sparas aggregerar och
                                av-identifierar AI materialet – personuppgifter och individnivå raderas automatiskt.
                                Din råfil lagras aldrig. Konsulten granskar resultatet innan det används.
                            </p>
                            <textarea
                                data-testid="insamling-paste-input"
                                rows={6}
                                value={pasteText}
                                onChange={(e) => { setPasteText(e.target.value); setFilnamn(""); }}
                                className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2.5 font-mono text-xs text-slate-100 outline-none focus:border-current/60 transition-colors"
                                placeholder="Klistra in exporten här (CSV/text) …"
                            />
                            <div className="flex items-center gap-2">
                                <button data-testid="insamling-file-button" onClick={() => fileRef.current?.click()}
                                    className="flex items-center gap-2 border border-dashed border-border rounded-lg px-3 py-2 text-xs text-slate-400 hover:border-current/40 hover:text-current transition-colors">
                                    <FileUp className="w-4 h-4" /> Välj fil (.csv, .txt)
                                </button>
                                <input ref={fileRef} data-testid="insamling-file-input" type="file" accept=".csv,.txt,.tsv,.md" className="sr-only" onChange={onFile} />
                                {filnamn && <span className="font-mono text-[10px] text-buoy">{filnamn}</span>}
                            </div>
                            <label className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed cursor-pointer">
                                <input data-testid="insamling-consent" type="checkbox" checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-emerald-400" />
                                Jag godkänner att denna data delas för organisatorisk analys, och att den av-identifieras
                                och aggregeras innan den sparas.
                            </label>
                            {error && <p data-testid="insamling-error" className="text-sm text-red-400">{error}</p>}
                            <button data-testid="insamling-submit-button" onClick={submit} disabled={sending || !consent || !pasteText.trim()}
                                className="w-full flex items-center justify-center gap-2 bg-current text-abyss font-semibold text-sm rounded-lg py-3 hover:bg-tide transition-colors disabled:opacity-40">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {sending ? "Bearbetar & av-identifierar …" : "Skicka material"}
                            </button>
                        </div>
                    )}

                    {done && (
                        <div data-testid="insamling-thanks" className="text-center py-6">
                            <CheckCircle2 className="w-10 h-10 text-buoy mx-auto mb-3" />
                            <h2 className="font-display font-bold text-lg text-slate-100">Tack!</h2>
                            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                                Materialet har aggregerats och av-identifierats. Din råfil är raderad.
                                Konsulten granskar resultatet innan det används i analysen.
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-600 mt-6">
                    HAMN ATLAS · godkännandebaserad insamling · rådata lagras aldrig
                </p>
            </div>
        </div>
    );
}
